import { isQuoteAction, quoteDecisionPermission } from "../qv4/models/price-drawer/quotePricingPolicy";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  List, Tag, Button, Tabs, Spin, Empty, Modal, Input, message, Space, Tooltip, theme,
} from "antd";
import {
  UserOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { API_DOMAIN } from "../constants";
import { QuoteApprovalModal } from "../qv4/views/price-drawer/QuoteApprovalModal";
import { OperationsApprovalModal, OPERATIONS_APPROVAL_META } from "./OperationsApprovalModal";
import { useAccess } from "../../features/access/context/AccessProvider";
import InvoiceRevisionReview from './InvoiceRevisionReview';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const dubaiFakeNow = () => dayjs.utc().add(4, "hour");
const fmtTs = (ts) => {
  if (!ts) return "—";
  const d = dayjs.utc(ts.replace(" ", "T"));
  return `${d.format("DD MMM · hh:mm A")} · ${d.from(dubaiFakeNow())}`;
};

const STATUS_COLOR = {
  pending: "orange",
  approved: "green",
  rejected: "red",
  cancelled: "default",
  request_changes: "blue",
  changes_requested: "blue",
  escalated: "purple",
};

const ACTION_COLOR = {
  quote_send: "blue",
  quote_send_below_recommended_gp: "orange",
  discount_approval: "purple",
  purchase_before_payment: "cyan",
  refund_payment: "orange",
  delete_payment: "red",
  ...Object.fromEntries(Object.entries(OPERATIONS_APPROVAL_META).map(([key, meta]) => [key, meta.color])),
};

const isOperationsRequest = request => String(request?.action_key || "").startsWith("operations_v2_");

function requestSummary(request) {
  const payload = request?.modified_payload ?? request?.payload ?? {};
  if (request?.action_key === "operations_v2_authorize_fulfillment") {
    return `${String(payload.authorization_basis || "Authorization").replaceAll("_", " ")} · AED ${Number(payload.due_amount || 0).toLocaleString("en-AE", { minimumFractionDigits: 2 })} due · ${payload.lines?.length || 0} lines`;
  }
  if (request?.action_key === "operations_v2_purchase_price_variance") return `Order #${payload.operations_order_id || "—"} · Procurement attempt #${payload.procurement_attempt_id || "—"}`;
  if (request?.action_key === "operations_v2_customer_warranty_resolution") return `Order #${payload.operations_order_id || "—"} · ${String(payload.resolution || "Warranty resolution").replaceAll("_", " ")}`;
  if (request?.action_key === "operations_v2_customer_change_refund_adjustment") return `Order #${payload.operations_order_id || "—"} · AED ${Number(payload.approved_refund_amount || 0).toLocaleString("en-AE", { minimumFractionDigits: 2 })} proposed refund`;
  if (request?.action_key === "operations_v2_residual_inventory_disposition") return `Order #${payload.operations_order_id || "—"} · ${String(payload.disposition || "Inventory decision").replaceAll("_", " ")}`;
  return null;
}

export function RequestsTab({ open, userMap, authState, onRequestDecided, onCountsLoaded, refreshSignal = 0, onLoadingChange }) {
  const { can } = useAccess();
  const { token } = theme.useToken();
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [userPower, setUserPower] = useState({ priority: 0, level: null });
  const [rulesMap, setRulesMap] = useState({});

  // Quote approval modal state
  const [quoteTarget, setQuoteTarget] = useState(null);
  const [operationsTarget, setOperationsTarget] = useState(null);

  // Generic action modal state
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState("approve");
  const [actionReason, setActionReason] = useState("");
  const [actionOverridePriority, setActionOverridePriority] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const userId = authState?.id;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, powerRes, rulesRes] = await Promise.all([
        axios.get(`${API_DOMAIN}/approval-requests`, head),
        userId ? axios.get(`${API_DOMAIN}/users/${userId}/approval-power`, head) : Promise.resolve(null),
        axios.get(`${API_DOMAIN}/approval-rules`, head),
      ]);
      const allRequests = reqRes.data?.data ?? reqRes.data ?? [];
      setRequests(allRequests);
      if (powerRes) setUserPower(powerRes.data?.data ?? { priority: 0, level: null });
      const rm = {};
      (rulesRes.data?.data ?? rulesRes.data ?? []).forEach(r => { rm[r.action_key] = r; });
      setRulesMap(rm);
      // This fetch is unfiltered/authoritative — a good moment to let
      // ApprovalCenter's bell-icon badge resync too, since that count is
      // otherwise only ever incremented/decremented off socket events and
      // can drift if one was missed (e.g. the tab was asleep when someone
      // else actioned a request).
      onCountsLoaded?.(allRequests.filter(r => r.status === "pending").length);
    } catch { message.error("Failed to load approval requests"); }
    finally { setLoading(false); }
  }, [userId]); // eslint-disable-line

  useEffect(() => { if (open) fetchAll(); }, [open, refreshSignal]); // eslint-disable-line

  useEffect(() => { onLoadingChange?.(loading); }, [loading, onLoadingChange]);

  const filtered = useMemo(() => {
    if (filter === "pending") return requests.filter(r => r.status === "pending");
    if (filter === "decided") return requests.filter(r => r.status !== "pending");
    return requests;
  }, [requests, filter]);

  const canAct = (req) => can("approvals.decide") && (!isQuoteAction(req.action_key) || can(quoteDecisionPermission(req.action_key))) && userPower.priority >= (req.required_priority ?? 0) && req.status === "pending";

  const handleReviewQuote = (req) => setQuoteTarget(req);

  const openGenericAction = (req, type) => {
    setActionTarget(req);
    setActionType(type);
    setActionReason("");
    setActionOverridePriority(null);
  };

  const submitGenericAction = async () => {
    if ((actionType === "reject" || actionType === "request_changes" || actionType === "cancel" || actionType === "escalate") && !actionReason.trim()) {
      message.warning("Please enter a reason");
      return;
    }
    setActionLoading(true);
    try {
      const body = {
        action: actionType,
        decision_reason: actionReason.trim() || null,
      };
      if (actionType === "escalate" && actionOverridePriority) {
        body.override_priority = actionOverridePriority;
      }
      await axios.post(`${API_DOMAIN}/approval-requests/${actionTarget.id}/action`, body, head);
      message.success(`Request ${actionType}d`);
      setRequests(prev => prev.map(r =>
        r.id === actionTarget.id ? { ...r, status: actionType === "approve" ? "approved" : actionType === "reject" ? "rejected" : actionType } : r
      ));
      onRequestDecided?.(actionTarget.id, actionType);
      setActionTarget(null);
    } catch (e) {
      message.error(e?.response?.data?.message || "Action failed");
    } finally { setActionLoading(false); }
  };

  const handleQuoteDone = (result, approvalId) => {
    setRequests(prev => prev.map(r =>
      r.id === approvalId ? { ...r, status: result } : r
    ));
    onRequestDecided?.(approvalId, result);
    setQuoteTarget(null);
  };

  const handleOperationsDone = (result, approvalId, options = {}) => {
    setRequests(prev => prev.map(r => r.id === approvalId ? { ...r, status: result } : r));
    onRequestDecided?.(approvalId, result);
    if (!options.keepOpen) setOperationsTarget(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <Tabs activeKey={filter} onChange={setFilter} size="small" style={{ flexShrink: 0 }}
        items={[{ key: "pending", label: "Pending" }, { key: "decided", label: "Decided" }, { key: "all", label: "All" }]} />

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin /></div>
      ) : filtered.length === 0 ? (
        <Empty description={`No ${filter} requests`} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
      ) : (
        <div style={{ overflowY: "auto", flex: 1 }}>
          <List
            dataSource={filtered}
            split={false}
            renderItem={req => {
              const rule = rulesMap[req.action_key];
              const submitterName = req.submitted_by ? (userMap?.[req.submitted_by] ?? `User #${req.submitted_by}`) : "—";
              const deciderName = req.decided_by ? (userMap?.[req.decided_by] ?? `User #${req.decided_by}`) : null;
              const isPending = req.status === "pending";
              const userCanAct = canAct(req);
              const isQuoteSend = isQuoteAction(req.action_key);
              const isOperations = isOperationsRequest(req);
              const operationsMeta = OPERATIONS_APPROVAL_META[req.action_key];
              const summary = requestSummary(req);

              return (
                <List.Item style={{ padding: "6px 0" }}>
                  <div style={{
                    width: "100%",
                    border: `1px solid ${isPending ? token.colorWarningBorder : req.status === "approved" ? token.colorSuccessBorder : req.status === "rejected" ? token.colorErrorBorder : token.colorBorder}`,
                    background: isPending ? token.colorWarningBg : req.status === "approved" ? token.colorSuccessBg : req.status === "rejected" ? token.colorErrorBg : token.colorBgContainer,
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}>
                    {/* Header row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <Space size={4} wrap>
                        <Tag color={ACTION_COLOR[req.action_key] ?? "default"} style={{ margin: 0 }}>
                          {operationsMeta?.label ?? rule?.action_label ?? req.action_key}
                        </Tag>
                        {req.query_id && (
                          <Tag style={{ margin: 0, fontWeight: 600 }}>#{req.query_id}</Tag>
                        )}
                      </Space>
                      <Tag color={STATUS_COLOR[req.status] ?? "default"} style={{ margin: 0, flexShrink: 0 }}>
                        {req.status}
                      </Tag>
                    </div>

                    {summary && <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 6, textTransform: "capitalize" }}>{summary}</div>}

                    {/* Meta */}
                    <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 4 }}>
                      <UserOutlined style={{ marginRight: 4 }} />
                      <span>Submitted by <strong style={{ color: token.colorTextSecondary }}>{submitterName}</strong></span>
                      <span style={{ margin: "0 6px" }}>·</span>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      {fmtTs(req.submitted_at)}
                    </div>

                    {/* Required level */}
                    <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 6 }}>
                      Required:{" "}
                      <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>
                        Priority {req.required_priority}
                      </Tag>
                      {userPower.priority > 0 && (
                        <span style={{ marginLeft: 4 }}>
                          · Your power:{" "}
                          <Tag color={userCanAct ? "green" : "default"} style={{ fontSize: 10, margin: 0 }}>
                            Priority {userPower.priority}
                          </Tag>
                        </span>
                      )}
                    </div>

                    {/* Decided info */}
                    {!isPending && deciderName && (
                      <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 4 }}>
                        {req.status === "approved" ? <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: 4 }} /> : <CloseCircleOutlined style={{ color: token.colorError, marginRight: 4 }} />}
                        {req.status} by <strong style={{ color: token.colorTextSecondary }}>{deciderName}</strong>
                        {req.decided_at && <span> · {fmtTs(req.decided_at)}</span>}
                      </div>
                    )}
                    {!isPending && req.decision_reason && (
                      <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 4 }}>
                        Reason: {req.decision_reason}
                      </div>
                    )}

                    {/* Action buttons */}
                    {isPending && userCanAct && (
                      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                        {isQuoteSend ? (
                          <Button type="primary" size="small" onClick={() => handleReviewQuote(req)}>
                            Review Quote
                          </Button>
                        ) : isOperations ? (
                          <Button type="primary" size="small" onClick={() => setOperationsTarget(req)}>
                            Review {operationsMeta?.label ?? "Operations Request"}
                          </Button>
                        ) : (
                          <>
                            <Button type="primary" size="small" onClick={() => openGenericAction(req, "approve")}>
                              Approve
                            </Button>
                            <Button danger size="small" onClick={() => openGenericAction(req, "reject")}>
                              Reject
                            </Button>
                            <Button size="small" onClick={() => openGenericAction(req, "request_changes")}>
                              Request Changes
                            </Button>
                          </>
                        )}
                        {rule?.allow_override && (
                          <Tooltip title="Escalate to higher approver">
                            <Button size="small" onClick={() => openGenericAction(req, "escalate")}>
                              Escalate
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    )}
                    {isPending && !userCanAct && (
                      <div style={{ fontSize: 11, color: token.colorTextDisabled, marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        <QuestionCircleOutlined />
                        {(!can("approvals.decide") || (isQuoteSend && !can(quoteDecisionPermission(req.action_key)))) ? "Your access preset does not allow this approval decision" : "You don't have sufficient approval power for this request"}
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      )}

      {/* Quote approval modal */}
      <QuoteApprovalModal
        open={!!quoteTarget}
        onClose={() => setQuoteTarget(null)}
        approval={quoteTarget}
        effectivePriority={userPower.priority}
        authState={authState}
        userId={userId}
        onDone={handleQuoteDone}
      />

      <OperationsApprovalModal
        open={!!operationsTarget}
        approval={operationsTarget}
        userId={userId}
        userMap={userMap}
        onClose={() => setOperationsTarget(null)}
        onDone={handleOperationsDone}
      />

      {/* Generic action modal */}
      <Modal
        open={!!actionTarget}
        title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1).replace("_", " ")} Request`}
        onCancel={() => setActionTarget(null)}
        onOk={submitGenericAction}
        confirmLoading={actionLoading}
        okButtonProps={{ danger: actionType === "reject" || actionType === "cancel" }}
        okText={actionType.charAt(0).toUpperCase() + actionType.slice(1).replace("_", " ")}
        destroyOnHidden
      >
        {actionTarget && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
            <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
              <Tag color={ACTION_COLOR[actionTarget?.action_key] ?? "default"}>
                {rulesMap[actionTarget?.action_key]?.action_label ?? actionTarget?.action_key}
              </Tag>
              {actionTarget?.query_id && <Tag>#{actionTarget.query_id}</Tag>}
            </div>
            {actionTarget.action_key === 'payments_v2_invoice_revision_decrease' && <InvoiceRevisionReview payload={actionTarget.payload} />}
            {actionType === "escalate" && (
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: token.colorTextSecondary }}>Override Priority (optional)</div>
                <Input
                  type="number"
                  placeholder="e.g. 30"
                  value={actionOverridePriority ?? ""}
                  onChange={e => setActionOverridePriority(e.target.value ? Number(e.target.value) : null)}
                />
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, marginBottom: 4, color: token.colorTextSecondary }}>
                Reason {(actionType === "approve") ? "(optional)" : "(required)"}
              </div>
              <Input.TextArea
                rows={3}
                placeholder="Enter reason..."
                value={actionReason}
                onChange={e => setActionReason(e.target.value)}
                autoFocus
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
